export interface HttpResponse {
  ok: boolean;
  status: number;
  statusText: string;
  json: () => Promise<unknown>;
  text: () => Promise<string>;
}

export interface HttpRequestOptions {
  method?: string;
  headers?: { [key: string]: string };
  body?: string;
  // External cancellation (the Stop button / clicking the typing-indicator bubble — see
  // chat.ts) — the only way a request in flight ever ends early. No internal timeout: some
  // models (extended-reasoning/"thinking" modes especially) can legitimately take several
  // minutes, and even with streaming (httpStream below) giving live partial feedback while
  // waiting, there is still no safe fixed cutoff to guess at — a request just waits until it
  // resolves, errors, or the author cancels it themselves.
  signal?: AbortSignal;
}

export async function httpRequest(url: string, options: HttpRequestOptions): Promise<HttpResponse> {
  let response: Response;
  try {
    response = await fetch(url, {
      method: options.method || "GET",
      headers: options.headers,
      body: options.body,
      signal: options.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      // Genuine cancellation (Stop button/typing-bubble click) — propagate as-is so the
      // caller's own AbortError handling applies.
      throw err;
    }
    throw new Error("Network request failed.");
  }

  return {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    json: () => response.json(),
    text: () => response.text(),
  };
}

// One parsed Server-Sent-Events frame — `event` is the optional `event:` line (absent for
// providers, like OpenAI, that never send one and rely on `data:` alone), `data` is every
// `data:` line in the frame joined with "\n" per the SSE spec (in practice always a single
// line for the providers this app targets, since they each send one JSON object per frame).
export interface SseEvent {
  event?: string;
  data: string;
}

export interface HttpStreamResponse {
  ok: boolean;
  status: number;
  statusText: string;
  // Error path only (mirrors HttpResponse's own json()/text() above) — read the plain error
  // body the same way a non-streaming request would (adapters/types.ts's safeErrorDetail calls
  // json() on this exactly like it does on a plain HttpResponse), without ever touching the
  // body-reader/SSE path below.
  json: () => Promise<unknown>;
  text: () => Promise<string>;
  // Only meaningful when `ok` — iterates parsed SSE frames as they arrive. Provider-agnostic:
  // doesn't know or care what a given `data:` payload means (JSON shape, `[DONE]` sentinel,
  // event-type dispatch) — that's each adapter's own job, same as this file already leaves
  // response-body *shape* entirely to callers of httpRequest above.
  events: () => AsyncGenerator<SseEvent>;
}

// Streaming counterpart to httpRequest above — same fetch/error/abort handling (deliberately
// duplicated rather than sharing a helper with httpRequest, since the two functions' success
// paths diverge completely — one hands back thunks for a single json()/text() read, the other
// hands back a live frame-by-frame generator — and this file's own convention elsewhere favors
// small self-contained pieces over a shared abstraction once two things only look alike).
// httpRequest itself is untouched — listModels() on every adapter keeps using it as before,
// only sendMessage()'s now-streaming request path uses this.
export async function httpStream(
  url: string,
  options: HttpRequestOptions
): Promise<HttpStreamResponse> {
  let response: Response;
  try {
    response = await fetch(url, {
      method: options.method || "GET",
      headers: options.headers,
      body: options.body,
      signal: options.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw err;
    }
    throw new Error("Network request failed.");
  }

  async function* events(): AsyncGenerator<SseEvent> {
    if (!response.body) return;
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    // Shared by the main per-chunk loop and the end-of-stream flush below, so both parse a raw
    // frame's event:/data: lines identically.
    function parseFrame(rawFrame: string): SseEvent | undefined {
      let event: string | undefined;
      const dataLines: string[] = [];
      for (const rawLine of rawFrame.split("\n")) {
        // \r\n line endings (defensive — real provider APIs observed so far use bare \n, but
        // nothing about the SSE spec guarantees that).
        const line = rawLine.endsWith("\r") ? rawLine.slice(0, -1) : rawLine;
        if (line.startsWith("event:")) {
          event = line.slice(6).trimStart();
        } else if (line.startsWith("data:")) {
          dataLines.push(line.slice(5).trimStart());
        }
        // id:/retry: lines and blank/comment lines within a frame are deliberately ignored —
        // nothing in this app's three adapters needs them.
      }
      return dataLines.length > 0 ? { event, data: dataLines.join("\n") } : undefined;
    }

    try {
      while (true) {
        // A pending read rejects with the same AbortError fetch() itself would on cancellation
        // (the same AbortSignal governs the whole response lifecycle, not just headers) — left
        // to propagate uncaught, same as httpRequest's own fetch() above, so callers' existing
        // AbortError handling applies unchanged.
        const { done, value } = await reader.read();
        if (done) break;
        // `{ stream: true }` is required, not optional — a multi-byte UTF-8 character (routine
        // for the Chinese text this app's own audience writes) can straddle a chunk boundary,
        // and without this flag a split character decodes as a lossy replacement char instead
        // of being correctly buffered until its remaining bytes arrive.
        buffer += decoder.decode(value, { stream: true });

        // Matched with a regex, not `indexOf("\n\n")` — a server that terminates frames with
        // `\r\n\r\n` (observed from Gemini's stream; DeepSeek/Kimi/OpenAI-compatible backends use
        // bare `\n\n`) would never satisfy a literal "\n\n" search, since "\r\n\r\n" doesn't
        // contain two adjacent \n characters. That silently starved the per-chunk loop of every
        // frame boundary, deferring everything to the end-of-stream flush below, which then
        // handed a multi-frame blob to parseFrame/JSON.parse as if it were one frame — parse
        // failure, frame dropped, exactly the empty-text "unexpected shape" symptom this was
        // chasing.
        let boundaryMatch: RegExpMatchArray | null;
        while ((boundaryMatch = buffer.match(/\r?\n\r?\n/)) !== null) {
          // A successful non-global .match() always sets .index — the `?? 0` is just to satisfy
          // the type checker's (overly conservative here) `number | undefined`, not a real
          // fallback path.
          const boundaryIndex = boundaryMatch.index ?? 0;
          const rawFrame = buffer.slice(0, boundaryIndex);
          buffer = buffer.slice(boundaryIndex + boundaryMatch[0].length);
          const frame = parseFrame(rawFrame);
          if (frame) yield frame;
        }
      }
      // Real bug, found live against Gemini's alt=sse stream: not every SSE server sends a
      // trailing blank-line terminator after its very last event before closing the connection
      // — the frame-boundary loop above only ever yields on `\n\n`, so a final chunk with no
      // trailing separator was silently discarded in its entirety, not just delayed. Flush
      // whatever's left in the buffer as one last frame once the stream is actually done, rather
      // than dropping it.
      if (buffer) {
        const frame = parseFrame(buffer);
        if (frame) yield frame;
      }
    } finally {
      reader.releaseLock();
    }
  }

  return {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    json: () => response.json(),
    text: () => response.text(),
    events,
  };
}
